import { useState } from 'react';
import Swal from 'sweetalert2';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export const useDateRangeFilter = (updateFilters) => {
    const { t } = useTranslation();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const handleDateRangeFilter = async () => {
        // Format dates to YYYY-MM-DD for HTML date input
        const formatDateForInput = (date) => {
            if (!date) return '';
            return moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : '';
        };

        const currentFromDate = formatDateForInput(fromDate);
        const currentToDate = formatDateForInput(toDate);

        const result = await Swal.fire({
            title: t('customers.list.dateFilter.title'),
            html: `
            <div style="text-align: left;">
                <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: bold;">${t('customers.list.dateFilter.fromDate')}:</label>
                <input type="date" id="swal-from-date" value="${currentFromDate}" 
                       class="swal2-input" style="width: 90%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 14px;">
                </div>
                <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; font-weight: bold;">${t('customers.list.dateFilter.toDate')}:</label>
                <input type="date" id="swal-to-date" value="${currentToDate}" 
                       class="swal2-input" style="width: 90%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 14px;">
                </div>
                <div style="margin-top: 10px; padding: 10px; background-color: #f8f9fa; border-radius: 4px; font-size: 12px; color: #6c757d;">
                <strong>${t('customers.list.dateFilter.note')}</strong>
                </div>
            </div>
            `,
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('customers.list.dateFilter.apply'),
            denyButtonText: t('customers.list.dateFilter.clear'),
            cancelButtonText: t('customers.list.dateFilter.cancel'),
            confirmButtonColor: '#3085d6',
            denyButtonColor: '#d33',
            focusConfirm: false,
            preConfirm: () => {
            const selectedFromDate = document.getElementById('swal-from-date')?.value || '';
            const selectedToDate = document.getElementById('swal-to-date')?.value || '';
            
            // Validate date range
            if (selectedFromDate && selectedToDate && selectedFromDate > selectedToDate) {
                Swal.showValidationMessage(t('customers.list.dateFilter.invalidRange'));
                return false;
            }
            
            // Ensure returned dates are formatted YYYY-MM-DD
            return { 
                fromDate: selectedFromDate ? moment(selectedFromDate).format('YYYY-MM-DD') : '', 
                toDate: selectedToDate ? moment(selectedToDate).format('YYYY-MM-DD') : '' 
            };
            }
        });

        if (result.isConfirmed) {
            // Apply date filter
            const { fromDate: selectedFromDate, toDate: selectedToDate } = result.value;
            
            setFromDate(selectedFromDate);
            setToDate(selectedToDate);
            
            // Prepare filter object
            const dateFilters = {};
            if (selectedFromDate) {
                dateFilters.fromDate = selectedFromDate;
            }
            if (selectedToDate) {
                dateFilters.toDate = selectedToDate;
            }
            
            // Apply the filter (pass empty object if no dates selected)
            updateFilters(Object.keys(dateFilters).length > 0 ? dateFilters : { fromDate: null, toDate: null });
        } else if (result.isDenied) {
            // Clear date filter
            setFromDate('');
            setToDate('');
            updateFilters({ fromDate: null, toDate: null });
        }
        // If result.isDismissed (cancel), do nothing
    };

    const getDateRangeDisplay = () => {
        if (!fromDate && !toDate) {
            return '';
        }

        const formatDate = (date) => date ? moment(date).format('YYYY-MM-DD') : '';

        if (fromDate && toDate) {
            return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
        } else if (fromDate) {
            return `From: ${formatDate(fromDate)}`;
        } else if (toDate) {
            return `Until: ${formatDate(toDate)}`;
        }
        
        return '';
    };

    return {
        fromDate,
        toDate,
        handleDateRangeFilter,
        getDateRangeDisplay
    };
};
